import React, { useState } from 'react';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Avatar
} from '@mui/material';
import { Business, Refresh } from '@mui/icons-material';
import useClient from '../redux/hooks/useClient';
import { useAuth } from '../redux/hooks/useAuth';

const ClientSelector = ({ 
  variant = 'outlined', 
  size = 'medium',
  showLabel = true,
  showStatus = true,
  onClientChange = null
}) => {
  const {isSuperAdmin} = useAuth();
  const {
    currentClient,
    availableClients,
    loading,
    switchToClient
  } = useClient();

  const [open, setOpen] = useState(false);

  // Don't render if user can't switch clients
  if (!isSuperAdmin) {
    return null;
  }

  const handleClientChange = async (event) => {
    const newClientId = event.target.value;
    if (newClientId === currentClient?.client_id) return;

    try {
      await switchToClient(newClientId);
      if (onClientChange) {
        onClientChange(newClientId);
      }
    } catch (error) {
      console.error('Failed to switch client:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'inactive':
        return 'Inactive';
      case 'pending':
        return 'Pending';
      default:
        return 'Unknown';
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
       <Tooltip title="Refresh clients">
        <IconButton
          size="small"
          onClick={() => window.location.reload()}
          disabled={loading}
          sx={{
            color: 'text.secondary',
            '&:hover': { 
              backgroundColor: 'action.hover',
              color: 'primary.main'
            }
          }}
        >
          <Refresh sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
      <FormControl 
        size="small"
        variant="outlined"
        sx={{ minWidth: 180 }}
        disabled={loading}
      >
        <Select
          value={currentClient?.client_id || ''}
          onChange={handleClientChange}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          open={open}
          displayEmpty
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 1.5,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'divider',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'primary.main',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'primary.main',
            }
          }}
          renderValue={(selected) => {
            if (!selected) {
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Business sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Select Client
                  </Typography>
                </Box>
              );
            }

            const client = Array.isArray(availableClients) ? availableClients.find(c => c.client_id === selected) : null;
            if (!client) return 'Unknown Client';
            return (
        
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                
                <Avatar 
                  sx={{ 
                    width: 20, 
                    height: 20, 
                    bgcolor: 'primary.main',
                    fontSize: '0.75rem',
                    fontWeight: 600
                  }}
                >
                  {(client.company_name || client.name || 'C').charAt(0).toUpperCase()}
                </Avatar>
                <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                  {client.company_name || client.name || 'Unknown Client'}
                </Typography>
            
              </Box>
            );
          }}
        >
          {Array.isArray(availableClients) && availableClients.length > 0 ? (
            availableClients.map((client) => (
              <MenuItem key={client.client_id} value={client.client_id} sx={{ py: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                  <Avatar 
                    sx={{ 
                      width: 24, 
                      height: 24, 
                      bgcolor: 'primary.main',
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}
                  >
                    {(client.company_name || client.name || 'C').charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {client.company_name || client.name || 'Unknown Client'}
                    </Typography>
                 
                  </Box>
          
                </Box>
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                No clients available
              </Typography>
            </MenuItem>
          )}
        </Select>
      </FormControl>

      {loading && (
        <CircularProgress size={16} sx={{ color: 'text.secondary' }} />
      )}

     
    </Box>
  );
};

export default ClientSelector;
